import { useEffect, useState, type FormEvent } from 'react'
import { useAuth, type TwoFactorChallenge } from '../hooks/useAuth.js'
import { TwoFactorStep } from '../components/TwoFactorStep.js'
import { api, apiBase, ApiError } from '../lib/api.js'
import { Notice, Ticks } from '../components/ui.js'
import {
  ArrowRightIcon,
  BoltIcon,
  EyeIcon,
  EyeOffIcon,
  GoogleIcon,
  LockIcon,
  MailIcon,
  PhoneIcon,
  ShieldIcon,
  ClockIcon,
  TrendUpIcon,
} from '../components/icons.js'

/** Why Google sent the merchant back here — `?google=` from the API's callback. */
const GOOGLE_REASONS: Record<string, { kind: 'info' | 'error'; text: string }> = {
  google_cancelled: { kind: 'info', text: 'Google sign-in was cancelled.' },
  google_failed: { kind: 'error', text: 'Google sign-in did not complete. Please try again.' },
  google_unavailable: { kind: 'error', text: 'Google sign-in is not available right now. Use your mobile number.' },
  google_unverified: { kind: 'error', text: 'That Google account has no verified email address.' },
  google_no_account: {
    kind: 'info',
    text: 'No account uses that Google address yet. Choose "Sign up" and continue with Google to make one.',
  },
}

const STATS = [
  { icon: <TrendUpIcon size={15} />, value: '8.4%', label: 'average conversion' },
  { icon: <ClockIcon size={15} />, value: '1.2s', label: 'checkout load' },
  { icon: <ShieldIcon size={15} />, value: '99.9%', label: 'uptime' },
]

/** Seconds before "Resend code" becomes clickable again. */
function useCountdown(from: number): [number, (seconds: number) => void] {
  const [left, setLeft] = useState(0)

  useEffect(() => {
    if (left <= 0) return undefined
    const timer = setTimeout(() => setLeft((value) => value - 1), 1000)
    return () => clearTimeout(timer)
  }, [left])

  useEffect(() => setLeft(from), [from])

  return [left, setLeft]
}

export function LoginPage(): React.ReactNode {
  const { signIn, signInWithOtp } = useAuth()

  /**
   * Phone first, because that is how these merchants actually sign in.
   *
   * Most never set a password — a Shopify store is onboarded from inside the
   * Shopify admin, long before anybody visits this screen, and the number an
   * operator already has on file is the thing they can prove. Email and password
   * stay available for the accounts that do have one.
   */
  const [mode, setMode] = useState<'phone' | 'password'>('phone')

  /**
   * Which door the merchant came through.
   *
   * The exchange behind both is identical — a number, a code — and the only
   * thing it changes is what happens to a number with no account behind it.
   * Signing *in* refuses; signing up creates. That is the entire reason to show
   * two doors rather than one form: on one form a mistyped digit is
   * indistinguishable from a new merchant, so it quietly built them a second,
   * empty account and signed them into it.
   */
  const [door, setDoor] = useState<'signin' | 'signup'>('signin')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [sent, setSent] = useState(false)
  const [resendAfter, setResendAfter] = useState(0)
  const [resendIn, setResendIn] = useCountdown(resendAfter)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [reveal, setReveal] = useState(false)
  const [remember, setRemember] = useState(true)
  const [error, setError] = useState<ApiError | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [googleEnabled, setGoogleEnabled] = useState(false)
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null)

  const googleReason = GOOGLE_REASONS[new URLSearchParams(window.location.search).get('google') ?? '']

  // Google sign-in cannot return a challenge in a response, so it arrives here by URL.
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('2fa')
    if (!token) return
    api<TwoFactorChallenge>(`/v1/auth/2fa/challenge/${encodeURIComponent(token)}`)
      .then(setChallenge)
      .catch((caught: unknown) => setError(caught instanceof ApiError ? caught : new ApiError(0, 'unknown', 'Sign-in expired.')))
  }, [])

  useEffect(() => {
    api<{ enabled: boolean }>('/v1/auth/google/status')
      .then((result) => setGoogleEnabled(result.enabled))
      .catch(() => setGoogleEnabled(false))
  }, [])

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)
    setNotice(null)
    setBusy(true)

    try {
      const pending = await signIn(email, password)
      if (pending) setChallenge(pending)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError(0, 'unknown', 'Something went wrong.'))
    } finally {
      setBusy(false)
    }
  }

  async function sendCode(event: FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)
    setNotice(null)
    setBusy(true)

    try {
      const result = await api<{ resend_after: number; debug_code: string | null }>('/v1/auth/otp/send', {
        method: 'POST',
        body: { phone },
      })

      setSent(true)
      setResendAfter(result.resend_after || 30)

      // Only ever set by a development channel that did not really send
      // anything — the API withholds it in production.
      if (result.debug_code) setNotice(`Development channel: your code is ${result.debug_code}`)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError(0, 'unknown', 'Could not send the code.'))
    } finally {
      setBusy(false)
    }
  }

  async function verifyCode(event: FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)
    setBusy(true)

    try {
      const pending = await signInWithOtp(phone, code, door)
      if (pending) setChallenge(pending)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError(0, 'unknown', 'Something went wrong.'))
    } finally {
      setBusy(false)
    }
  }

  // A passwordless account is not a failed sign-in, and saying so is the
  // difference between one click and a support ticket. The API distinguishes
  // them; this is the screen honouring that.
  const passwordless = error?.is('auth.passwordless_account') ?? false

  // Signing in with a number that has no account. Not a failure to hide — it is
  // the one thing the split exists to say out loud.
  const noAccount = error?.is('auth.no_account') ?? false

  if (challenge) {
    return (
      <TwoFactorStep
        challenge={challenge}
        onCancel={() => {
          setChallenge(null)
          setSent(false)
          setCode('')
          setPassword('')
          window.history.replaceState(null, '', '/login')
        }}
      />
    )
  }

  return (
    <div className="auth">
      <div className="auth__pane">
        <form
          className="auth__card"
          onSubmit={(event) => void (mode === 'password' ? submit(event) : sent ? verifyCode(event) : sendCode(event))}
        >
          <p className="auth__brand">
            <BoltIcon size={24} style={{ color: 'var(--brand)' }} />
            <span>Outfalak</span> Checkout
          </p>

          <div className="auth__tabs" role="tablist" aria-label="Sign in or create an account">
            {(['signin', 'signup'] as const).map((which) => (
              <button
                key={which}
                type="button"
                role="tab"
                aria-selected={door === which}
                className={`auth__tab${door === which ? ' is-active' : ''}`}
                onClick={() => {
                  setDoor(which)
                  setSent(false)
                  setCode('')
                  setError(null)
                  setNotice(null)
                  // Email and password only exist for an account that already
                  // has one; there is no way to create one here.
                  if (which === 'signup') setMode('phone')
                }}
              >
                {which === 'signin' ? 'Sign in' : 'Sign up'}
              </button>
            ))}
          </div>

          <h1>{door === 'signin' ? 'Welcome back' : 'Create your account'}</h1>
          <p className="auth__sub">
            {door === 'signin'
              ? 'Sign in to your merchant dashboard'
              : 'Your mobile number is your account — no password to choose'}
          </p>

          {error && (
            <Notice
              kind={passwordless || noAccount ? 'info' : 'error'}
              title={
                passwordless
                  ? 'This account has no password'
                  : noAccount
                    ? 'No account with that number'
                    : undefined
              }
            >
              {error.message}

              {/*
                A dead end otherwise. The merchant has already proved they own
                the number; making them find the other tab themselves is the
                whole cost of having two doors, and this pays it back.
              */}
              {noAccount && (
                <button
                  type="button"
                  className="btn btn--link"
                  style={{ marginTop: 8 }}
                  onClick={() => {
                    setDoor('signup')
                    setError(null)
                    // The code they hold is still good — only the refusal was
                    // about which door they knocked on.
                  }}
                >
                  Create an account with {phone}
                </button>
              )}
            </Notice>
          )}

          {googleReason && !notice && !error && <Notice kind={googleReason.kind}>{googleReason.text}</Notice>}
          {notice && <Notice kind="info">{notice}</Notice>}

          {mode === 'phone' && !sent && (
            <>
              <label className="field">
                <span>Mobile number</span>
                <div className="input-group">
                  <PhoneIcon size={16} />
                  <input
                    type="tel"
                    className="input"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    placeholder="98765 43210"
                    autoComplete="tel"
                    inputMode="numeric"
                    required
                    autoFocus
                  />
                </div>
              </label>

              <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={busy}>
                {busy ? 'Sending code…' : 'Send code'}
                {!busy && <ArrowRightIcon size={16} />}
              </button>

              <p className="auth__sub" style={{ textAlign: 'center', fontSize: '.82rem' }}>
                We will send a one-time code to this number on WhatsApp.
              </p>
            </>
          )}

          {mode === 'phone' && sent && (
            <>
              <label className="field">
                <span>Enter the code</span>
                <div className="input-group">
                  <LockIcon size={16} />
                  <input
                    type="text"
                    className="input"
                    value={code}
                    onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                    placeholder="6-digit code"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    maxLength={8}
                    required
                    autoFocus
                  />
                </div>
              </label>

              <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={busy}>
                {busy ? 'Signing in…' : 'Verify and sign in'}
                {!busy && <ArrowRightIcon size={16} />}
              </button>

              <div className="auth__row">
                <button
                  type="button"
                  className="btn btn--link"
                  onClick={() => {
                    setSent(false)
                    setCode('')
                    setError(null)
                  }}
                >
                  Change number
                </button>
                <button
                  type="button"
                  className="btn btn--link"
                  disabled={busy || resendIn > 0}
                  onClick={(event) => void sendCode(event)}
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
                </button>
              </div>
            </>
          )}

          {mode === 'password' && (
            <>
              <label className="field">
                <span>Email address</span>
                <div className="input-group">
                  <MailIcon size={16} />
                  <input
                    type="email"
                    className="input"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@yourstore.com"
                    autoComplete="username"
                    required
                    autoFocus
                  />
                </div>
              </label>

              <label className="field">
                <span>Password</span>
                <div className="input-group">
                  <LockIcon size={16} />
                  <input
                    type={reveal ? 'text' : 'password'}
                    className="input"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="auth__reveal"
                    onClick={() => setReveal((value) => !value)}
                    aria-label={reveal ? 'Hide password' : 'Show password'}
                    title={reveal ? 'Hide password' : 'Show password'}
                  >
                    {reveal ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
                  </button>
                </div>
              </label>

              <div className="auth__row">
                <label className="check">
                  <input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} />
                  <span>Remember me</span>
                </label>
                <button
                  type="button"
                  className="btn btn--link"
                  onClick={() => setNotice('Password reset is not enabled yet — contact support and we will reset it for you.')}
                >
                  Forgot password?
                </button>
              </div>

              <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
                {!busy && <ArrowRightIcon size={16} />}
              </button>
            </>
          )}

          {/* There is no password to create here, so the toggle is a sign-in
              affordance only. */}
          {door === 'signin' && <div className="auth__divider">or</div>}

          {door === 'signin' && (
          <button
            type="button"
            className="btn btn--block"
            onClick={() => {
              setMode(mode === 'phone' ? 'password' : 'phone')
              setSent(false)
              setCode('')
              setError(null)
              setNotice(null)
            }}
          >
            {mode === 'phone' ? <MailIcon size={16} /> : <PhoneIcon size={16} />}
            {mode === 'phone' ? 'Sign in with email and password' : 'Sign in with your mobile number'}
          </button>
          )}

          {/*
            A full-page navigation, not a fetch: the API hands the browser to
            Google, and Google's redirect back is what sets the session cookie.
            When the operator has not switched it on, the button says so rather
            than sending the merchant somewhere that fails.
          */}
          <button
            type="button"
            className="btn btn--block"
            style={{ marginTop: 10 }}
            onClick={() => {
              if (!googleEnabled) {
                setNotice('Google sign-in is not configured on this deployment yet. Use your mobile number.')
                return
              }
              window.location.href = `${apiBase}/v1/auth/google?mode=${door}`
            }}
          >
            <GoogleIcon size={17} />
            Continue with Google
          </button>

          <p className="auth__foot">
            Trouble signing in? <a href="mailto:support@outfalakcheckout.com">Contact support</a>
          </p>
        </form>
      </div>

      <aside className="auth__aside">
        <p className="auth__pill">
          <BoltIcon size={14} />
          All-in-one Checkout
        </p>

        <h2>
          One-click checkout that Indian shoppers <em>actually finish.</em>
        </h2>

        <p>
          Shopify, WooCommerce or a custom storefront — one integration for UPI, cards, COD with OTP, WhatsApp recovery and
          real-time analytics.
        </p>

        <Ticks
          items={[
            'One-page checkout with address autofill',
            'COD with smart rules and OTP verification',
            'Abandoned cart recovery over WhatsApp',
            'Real-time orders, revenue and RTO insight',
          ]}
        />

        <div className="auth__stats">
          {STATS.map((stat) => (
            <div className="auth__stat" key={stat.label}>
              <span>{stat.icon}</span>
              <strong>{stat.value}</strong>
              <small>{stat.label}</small>
            </div>
          ))}
        </div>
      </aside>
    </div>
  )
}
